/**
 * Color utility functions for the shared chat widget
 */

/**
 * Calculate relative luminance of a hex color (0 = dark, 1 = light)
 * Used to determine if icons should be light or dark on a given background.
 */
export function getColorLuminance(hex: string): number {
  // Remove # if present, handle potential gradient/CSS values
  const cleanHex = hex.replace('#', '').substring(0, 6);
  if (!/^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    return 0.5; // Default to neutral if invalid
  }

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
}

/**
 * Get contrasting icon color for a given background: whichever of white and black has the
 * higher WCAG contrast against it, so the icon reaches at least 4.58:1 on any background.
 *
 * The earlier rule switched at a luminance of 0.4 to a near-black #09090B; the crossover
 * is at about 0.18, so a mid-light header such as #0EA5E9 got white icons at 2.77:1
 * (TASK-BUGFIX-WIDGET-CONTRAST-PAIRS-FOLLOWUPS).
 */
export function getContrastingIconColor(backgroundHex: string): string {
  const luminance = getColorLuminance(backgroundHex);
  const againstWhite = 1.05 / (luminance + 0.05);
  const againstBlack = (luminance + 0.05) / 0.05;
  return againstWhite >= againstBlack ? '#FFFFFF' : '#000000';
}

/**
 * Parse a hex color to RGB values
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return null;

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return { r, g, b };
}

/**
 * Lighten a hex color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const lighten = (value: number) => {
    const newValue = Math.round(value + (255 - value) * (percent / 100));
    return Math.min(255, Math.max(0, newValue));
  };

  const r = lighten(rgb.r).toString(16).padStart(2, '0');
  const g = lighten(rgb.g).toString(16).padStart(2, '0');
  const b = lighten(rgb.b).toString(16).padStart(2, '0');

  return `#${r}${g}${b}`;
}

/**
 * Convert a hex color to HSL values.
 *
 * @param hex - Color in hex format (#RRGGBB or RRGGBB)
 * @returns HSL object with h (0–360), s (0–1), l (0–1), or null if invalid
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return { h: h * 360, s, l };
}

/**
 * Convert HSL values to a hex color string.
 *
 * @param h - Hue (0–360)
 * @param s - Saturation (0–1)
 * @param l - Lightness (0–1)
 * @returns Hex color string (#RRGGBB)
 */
export function hslToHex(h: number, s: number, l: number): string {
  const hNorm = ((h % 360) + 360) % 360;

  if (s === 0) {
    const val = Math.round(l * 255);
    const hex = val.toString(16).padStart(2, '0');
    return `#${hex}${hex}${hex}`;
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    let tNorm = t;
    if (tNorm < 0) tNorm += 1;
    if (tNorm > 1) tNorm -= 1;
    if (tNorm < 1 / 6) return p + (q - p) * 6 * tNorm;
    if (tNorm < 1 / 2) return q;
    if (tNorm < 2 / 3) return p + (q - p) * (2 / 3 - tNorm) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hFrac = hNorm / 360;

  const r = Math.round(hue2rgb(p, q, hFrac + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, hFrac) * 255);
  const b = Math.round(hue2rgb(p, q, hFrac - 1 / 3) * 255);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Generate a CSS gradient background
 */
export function getGradientCSS(
  baseColor: string,
  direction: number,
  intensity: number
): string {
  const lighterColor = lightenColor(baseColor, intensity);
  return `linear-gradient(${direction}deg, ${lighterColor}, ${baseColor})`;
}

/**
 * Alpha-blend a foreground color over a background color.
 * Simulates what the user sees when a semi-transparent surface is over another color.
 *
 * @param fg - Foreground (surface) color hex
 * @param bg - Background (behind) color hex
 * @param alpha - Opacity of the foreground (0-1)
 * @returns Blended color as hex string
 */
export function blendColors(fg: string, bg: string, alpha: number): string {
  const fgRgb = hexToRgb(fg);
  const bgRgb = hexToRgb(bg);
  if (!fgRgb || !bgRgb) return fg;

  const a = Math.max(0, Math.min(1, alpha));
  const r = Math.round(fgRgb.r * a + bgRgb.r * (1 - a));
  const g = Math.round(fgRgb.g * a + bgRgb.g * (1 - a));
  const b = Math.round(fgRgb.b * a + bgRgb.b * (1 - a));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Compute the effective visible background color for WCAG contrast checking,
 * accounting for gradient lightening, glass transparency, and grain noise.
 *
 * @param color - Base hex color of the surface
 * @param options - Active visual effects on this surface
 * @returns Effective hex color representing worst-case visual appearance
 */
export function getEffectiveBackgroundForContrast(
  color: string,
  options: {
    /** Whether gradient is active on this surface */
    hasGradient?: boolean;
    /** Gradient lightening intensity (default 15) */
    gradientIntensity?: number;
    /** Active style effect type */
    styleEffectType?: 'none' | 'glass' | 'soft-shadow' | 'grain';
    /** Glass opacity 0-100 (default 30) */
    glassOpacity?: number;
    /** Grain intensity 0-100 (default 50) */
    grainIntensity?: number;
    /** Color behind this surface (for glass blending) */
    behindColor?: string;
  } = {}
): string {
  let result = color;

  // 1. Gradient: lighter end is worst case
  if (options.hasGradient) {
    result = lightenColor(result, options.gradientIntensity ?? 15);
  }

  // 2. Glass: blend with background at glass opacity
  if (options.styleEffectType === 'glass') {
    const alpha = (options.glassOpacity ?? 30) / 100;
    const behind = options.behindColor ?? '#FFFFFF';
    result = blendColors(result, behind, alpha);
  }

  // 3. Grain: noise shifts toward mid-gray
  if (options.styleEffectType === 'grain') {
    const grainOpacity = 0.03 + ((options.grainIntensity ?? 50) / 100) * 0.57;
    result = blendColors('#808080', result, grainOpacity * 0.5);
  }

  return result;
}

/**
 * Generate a CSS background value (gradient or solid color)
 *
 * @param color - Base color (hex)
 * @param useGradient - Whether to apply gradient
 * @param angle - Gradient angle in degrees (0-360)
 * @param intensity - Gradient intensity as percentage (0-50)
 * @returns CSS background value
 */
export function getBackgroundCSS(
  color: string,
  useGradient: boolean,
  angle: number = 180,
  intensity: number = 15
): string {
  if (!useGradient) {
    return color;
  }
  return getGradientCSS(color, angle, intensity);
}
